import { useEffect, useRef, useState } from 'react';
import { GlobalSearchParams, useGlobalSearchQuery } from 'store/api/globalSearch';
import { GlobalSearchMode, GlobalSearchResponse, isSearchableQuery, isTimedOutSearch } from 'types/globalSearch';
import { recentIdsHint } from 'utils/recentlyViewed';
import { captureSearchQueryRan, SearchSurface } from 'utils/searchAnalytics';
import { useDebouncedCallback } from './useDebouncedCallback';

/** Long enough that a fast typist issues one request per word, short enough to feel instant. */
export const GLOBAL_SEARCH_DEBOUNCE_MS = 200;

export interface UseGlobalSearchOptions {
    /** What is in the box. Trimmed here; nothing is fetched below the minimum length. */
    query: string;
    mode: GlobalSearchMode;
    /** Facet selections and the paging cursor, exactly as the endpoint takes them. */
    filters?: Omit<GlobalSearchParams, 'q' | 'mode' | 'recentIds'>;
    /** Which surface this is, for the analytics event. Defaults to the one the mode implies. */
    surface?: SearchSurface;
    /** Zero still settles on the next tick, which is what keeps a URL-driven page from double-firing. */
    debounceMs?: number;
    /** Hold the query without unmounting, e.g. while the dropdown is closed. */
    skip?: boolean;
}

export interface UseGlobalSearchResult {
    /**
     * The answer to {@link UseGlobalSearchResult.searchedQuery}, and never to anything else: it is
     * withheld while the box has moved on and the debounce has not caught up.
     */
    data: GlobalSearchResponse | undefined;
    /** Draw a skeleton from this — never a spinner. Covers the debounce window as well as the fetch. */
    isLoading: boolean;
    isError: boolean;
    /**
     * The error is the backend giving up on a query that matched too much to rank in time. Drawn as advice
     * to narrow the query rather than as an outage, since asking the same again fails the same way.
     */
    isTimedOut: boolean;
    /** The query is shorter than the backend answers, so nothing was asked. */
    isTooShort: boolean;
    /** The query `data` belongs to, which lags what is in the box by the debounce. */
    searchedQuery: string;
}

interface SettledQuery {
    query: string;
    /** Read once per settled query rather than per render — it comes out of `localStorage`. */
    recentIds: string[];
}

function settledFor(query: string): SettledQuery {
    return { query, recentIds: recentIdsHint() };
}

/**
 * The one search hook, for every surface.
 *
 * It fires once the backend would answer the query ({@link isSearchableQuery}), debounced, and attaches the
 * recently-viewed ranking hint. The results page uses it with the same shape and a different mode
 * and filters; it is deliberately not forked per surface.
 *
 * Nothing it hands back ever describes a different question from `searchedQuery`. RTK Query caches
 * per argument and this reads `currentData`, which belongs to the argument being asked for now, so
 * a slow response landing late fills its own cache entry and nothing else, and a facet change
 * empties the answer the moment it is made. The query itself reaches that argument only once the
 * debounce settles it, so the window in between is withheld here rather than guarded again on every
 * surface — one drawing it would put one query's rows, totals and facets under another's heading.
 */
export function useGlobalSearch({
    query,
    mode,
    filters,
    surface,
    debounceMs = GLOBAL_SEARCH_DEBOUNCE_MS,
    skip = false,
}: UseGlobalSearchOptions): UseGlobalSearchResult {
    const trimmed = query.trim();
    const [settled, setSettled] = useState<SettledQuery>(() => settledFor(trimmed));

    const settle = useDebouncedCallback((value: string) => {
        setSettled((current) => (current.query === value ? current : settledFor(value)));
    }, debounceMs);

    useEffect(() => {
        settle(trimmed);
    }, [trimmed, settle]);

    const isTooShort = !isSearchableQuery(trimmed);
    /*
     * The box has moved on and the debounce has not caught up, so `settled` — and with it the
     * argument being asked for, whatever is cached under it, and `searchedQuery` — all still
     * describe the query before this one.
     */
    const isSettling = trimmed !== settled.query;
    const shouldSkip = skip || !isSearchableQuery(settled.query);
    const { currentData, isFetching, isError, error } = useGlobalSearchQuery(
        { ...filters, q: settled.query, mode, recentIds: settled.recentIds },
        { skip: shouldSkip }
    );

    const resolvedSurface: SearchSurface = surface ?? (mode === 'DROPDOWN' ? 'dropdown' : 'results-page');

    // Latency is only knowable here, so the "a query ran" event is captured here rather than left to
    // each surface. A result served from cache issues no request and is no query run.
    const requestStartedAt = useRef<number | null>(null);
    useEffect(() => {
        if (isFetching) {
            requestStartedAt.current = Date.now();
            return;
        }
        const startedAt = requestStartedAt.current;
        requestStartedAt.current = null;
        if (startedAt === null || !currentData) {
            return;
        }
        captureSearchQueryRan({
            surface: resolvedSurface,
            queryLength: settled.query.length,
            resultCount: currentData.total,
            latencyMs: Date.now() - startedAt,
        });
    }, [isFetching, currentData, resolvedSurface, settled.query]);

    return {
        data: isTooShort || isSettling ? undefined : currentData,
        isLoading: !skip && !isTooShort && (isSettling || isFetching),
        isError,
        isTimedOut: isError && isTimedOutSearch(error),
        isTooShort,
        searchedQuery: settled.query,
    };
}
